/**
 * Level specification + gate math (pure, no Babylon). Plan 01: gate pairs are disguised arithmetic
 * comparisons; the greedy "bigger number / always ×" choice must lose; correct math must win with
 * margin. Design rules from research/gate-runner-genre-research.md.
 */

export interface GateSpec {
  /**
   * 'num' gates are shootable: every bullet hit raises value by 1 up to GATE_VALUE_CAP (negatives
   * flip positive). RAISE-ONLY — a printed value already above the cap is never pulled down to it
   * (plan 04 P1.2).
   *
   * 'mul' gates are the ×k multiplier (integer ≥ 2), 'div' gates the ÷k divider (divisor integer
   * ≥ 2, plan 04 P1.3). Neither is shootable — bullets pass straight through (tryHitGate skips
   * every non-'num' gate), and ÷ floors: N below the divisor walks through as 0.
   *
   * 'rate' is the weapon gate RATE+ (plan 11): N passes unchanged and the squad gains `value` (= 1)
   * rate pip. Not shootable; always paired against a `+k`, so it trades soldiers for cadence.
   */
  kind: 'num' | 'mul' | 'div' | 'rate';
  /** Signed for 'num' (e.g. -20, +12); the multiplier for 'mul'; the divisor for 'div'; pips for 'rate'. */
  value: number;
}

export interface GatePairSpec {
  z: number;
  left: GateSpec;
  right: GateSpec;
}

export interface CrateSpec {
  z: number;
  /** Bullet hits to break; grants the next weapon tier. */
  hp: number;
  /** Lateral center of the crate in [-1, 1]. */
  x: number;
}

export interface EnemySpec {
  z: number;
  count: number;
  /** Lateral center + half-width of the enemy block in [-1, 1] units. */
  x: number;
  halfWidth: number;
  /** Defaults to true; false suppresses the firepower pickup on a wipe. */
  dropReward?: boolean;
  /** Seconds between small-arms kills; zero lets each connected bullet kill. Defaults to cover suppression. */
  killInterval?: number;
}

/**
 * Static ground hazard (plan 04 P1.4): a fixed −penalty prop at (z, x) that the squad must steer
 * around. Rules: static (never moves, never respawns), one-shot (latches the first time the squad
 * crosses its z), and NOT shootable — bullets pass through because hazards are ground-level props
 * below the torso-height firing line (a gameplay rule, not geometry).
 */
export interface HazardSpec {
  z: number;
  /** Lateral center of the hazard band in [-1, 1] units. */
  x: number;
  /** Half-width of the hazard band in x-units (the squad footprint is added on top). */
  halfWidth: number;
  /** Soldiers lost when the formation overlaps the band at the crossing. */
  penalty: number;
}

/**
 * Upper bound on the squad formation's lateral half-width in x-units (plan 04 P1.4): 0.55 × 4.5 m
 * (BRIDGE_HALF_WIDTH) = 2.475 m. Since plan 10 this is the bound render/actors.ts `crowdPitch` is
 * BUILT to — every soldier centre stays within it at every tier and crowd size (`crowdPitch` also
 * caps a mid-size crowd at the legacy wedge's width, so only the High cap reaches it).
 *
 * Since plan 13 it is the CAP of `squadHalfX(n)`, which is what the sim tests and the Footprint
 * outline draws; level validation still uses the cap (dodgeable at the widest squad).
 */
export const SQUAD_FOOTPRINT_HALF_X = 1.1 / 2;

/**
 * Plan 13: the squad's lateral hit half-width at size n — grows with the crowd (√n, like its
 * area), capped at SQUAD_FOOTPRINT_HALF_X from n ≈ 52. Every squad-vs-thing x test in the sim uses
 * it (hazards, obstacles, blasts, enemy contact, pickups, slams), the Footprint marker draws it, and
 * `crowdPitch` keeps every soldier inside it. Level validation keeps using the cap (worst case).
 */
export function squadHalfX(n: number): number {
  return Math.min(SQUAD_FOOTPRINT_HALF_X, 0.14 + 0.057 * Math.sqrt(Math.max(1, n)));
}

/**
 * Plan 22: the squad centre's legal half-range at lateral half-width `halfX` — at the limit the
 * box EDGE (centre ± halfX) exactly kisses the deck rail, so the whole formation stays on the
 * bridge (the ask: "make the bridge a full constraining factor, no more cheating"). The stepSim
 * target clamp uses `steerLimit(squadHalfX(n))`; policies clamp their escape aims to the same
 * value. Owner decisions (grill 2026-10-21): dynamic at every size (no fixed worst-case range),
 * no hard clamp on squadX — an out-of-range centre (a +k gate just widened the box at the rail)
 * GLIDES back at lateralSpeed inside the ordinary movement step (worst transient: L1's +90 gate
 * grows n 10→100, shrinking the limit 0.68 → 0.45 = 0.23 x-units ≈ 1.03 m ≈ 105 ms).
 */
export function steerLimit(halfX: number): number {
  return 1 - halfX;
}

/**
 * Plan 13: a block wiped out by fire (or a blast) drops a firepower pickup this far past its z, at
 * its x. Walking over it (footprint within PICKUP_HALF_X) grants the next weapon tier, or a rate
 * pip once the ladder is maxed. One-shot: crossing its z without overlap misses it for good.
 */
export const PICKUP_AHEAD = 8;
export const PICKUP_HALF_X = 0.1;

/**
 * Deterministic slam pattern (L-wide, R-wide, C-narrow, repeating), x ∈ [-1, 1]. A slam lands when
 * the squad's footprint overlaps the zone (plan 13), so the centre zone is ±0.3, not ±0.45: a full
 * footprint (0.55) still clears it at |x| ≥ 0.86.
 */
export const SLAM_ZONES: [number, number][] = [
  [-1, 0.2],
  [-0.2, 1],
  [-0.3, 0.3],
];

/**
 * Firing columns (plan 10 B1): more soldiers ⇒ more guns. A squad of n fires `streamsFor(n)`
 * parallel copies of the tier's volley, one more per doubling from STREAM_BASE: 1 / 2 / 3 / 4
 * columns from 10 / 20 / 40 / 80 soldiers. Columns are extra bullets, not a damage multiplier, so
 * every per-bullet hit rule (gate +1, crate/boss damage, suppression, splash) is unchanged.
 * Geometry: (STREAM_MAX − 1)/2 × STREAM_PITCH + max|SPREAD| = 0.15 + 0.09 = 0.24 < 0.35, the crate
 * hit window, so every column hits a crate the squad is centred on.
 */
export const STREAM_BASE = 10;
export const STREAM_MAX = 4;
export const STREAM_PITCH = 0.1;

export function streamsFor(n: number): number {
  if (n < STREAM_BASE) return 1;
  return Math.min(STREAM_MAX, 1 + Math.floor(Math.log2(n / STREAM_BASE)));
}

/** Lateral offset of column k of `streams`, centred on the squad. */
export function streamOffset(k: number, streams: number): number {
  return (k - (streams - 1) / 2) * STREAM_PITCH;
}

/** The x-interval a hazard threatens, i.e. its band widened by the squad footprint. */
export function hazardThreatRange(hazard: HazardSpec, halfX = SQUAD_FOOTPRINT_HALF_X): [number, number] {
  return [hazard.x - hazard.halfWidth - halfX, hazard.x + hazard.halfWidth + halfX];
}

/** Would a formation centred at `x` (half-width `halfX`, default the cap) overlap this hazard? */
export function hazardOverlaps(hazard: HazardSpec, x: number, halfX = SQUAD_FOOTPRINT_HALF_X): boolean {
  const [lo, hi] = hazardThreatRange(hazard, halfX);
  return x >= lo && x <= hi;
}

/**
 * Safe formation-centre intervals for a hazard, widened by the squad footprint `halfX` (default
 * the CAP — validation's worst case) and clamped to the x-space range [-1, 1]. Empty means the
 * hazard cannot be dodged at all (it threatens the whole deck). The interval edges are pulled in
 * by HAZARD_DODGE_MARGIN because the overlap test is inclusive.
 *
 * Plan 22: these lanes are X-SPACE — a lane is only REACHABLE while the squad's on-deck steer
 * range covers it, i.e. while hazard.x ± hazard.halfWidth + halfX + HAZARD_DODGE_MARGIN <
 * steerLimit(halfX) on the relevant side (from n ≈ 14 a centre band's rail lanes sit outside the
 * range and the band is crowd-tax: every legal centre pays — owner decision 6). Policies search
 * these lanes at their ACTUAL size (`hazardEscape` passes squadHalfX(n)); validation and the
 * plan-04 dodgeability property deliberately keep the cap default (the worst case any shipped
 * squad can face).
 */
export const HAZARD_DODGE_MARGIN = 0.02;

export function hazardSafeRanges(hazard: HazardSpec, halfX = SQUAD_FOOTPRINT_HALF_X): [number, number][] {
  const [lo, hi] = hazardThreatRange(hazard, halfX);
  const ranges: [number, number][] = [];
  const leftHi = lo - HAZARD_DODGE_MARGIN;
  if (leftHi > -1) ranges.push([-1, leftHi]);
  const rightLo = hi + HAZARD_DODGE_MARGIN;
  if (rightLo < 1) ranges.push([rightLo, 1]);
  return ranges;
}

/**
 * Shootable obstacles (plan 09). Kinds are data, behaviours are two: a kind with a BLASTS row is
 * explosive (bullets or contact set it off, and the blast hits an x-interval), anything else is a
 * wall (it absorbs bullets; contact costs `ceil(hp × WALL_ATTRITION)` soldiers and breaks it).
 */
export type ObstacleKind = 'barrel' | 'barricade';

export interface ObstacleSpec {
  z: number;
  /** Lateral centre in [-1, 1] units; bullets hit within ±halfWidth, the squad adds its footprint. */
  x: number;
  halfWidth: number;
  /** Damage to break (the same damage units as crates: weapon damage × splash). */
  hp: number;
  kind: ObstacleKind;
}

export interface BlastSpec {
  /** The blast covers [x − halfWidth, x + halfWidth]; the squad adds SQUAD_FOOTPRINT_HALF_X. */
  halfWidth: number;
  /** Soldiers the squad loses per blast it is inside (chained blasts stack). */
  squadLoss: number;
  /** Soldiers an enemy block inside the interval loses. */
  enemyKill: number;
  /** Damage to another obstacle inside the interval (20 ≥ the shipped barrel hp 16, so barrels chain). */
  damage: number;
}

export const BLASTS: Partial<Record<ObstacleKind, BlastSpec>> = {
  // Plan 14: bigger boom. damage 20 ≥ the shipped barrel hp 16 (halfWidth 0.15), so chains hold.
  barrel: { halfWidth: 0.4, squadLoss: 20, enemyKill: 10, damage: 20 },
};

/**
 * z is time on a runner (10 m/s), so this is "the same 0.6 s moment", not a second spatial axis:
 * the only spatial extent of a blast is its x-interval.
 */
export const BLAST_REACH_Z = 6;

/** Plan-06 attrition: walking into a standing wall costs ceil(hp × this) soldiers and breaks it. */
export const WALL_ATTRITION = 0.5;

export interface BossSpec {
  z: number;
  hp: number;
  /** The squad stops this far from the boss. */
  standoff: number;
  /**
   * Brute: seconds between slams. Tank (plan 15): the FIRE cadence — the cycle is
   * silent → lock (telegraph) → fire (shell in flight) → detonation, all inside one period.
   */
  slamPeriod: number;
  /** Brute: telegraph window. Tank: the lock-to-fire window the squad has to escape the band. */
  slamTelegraph: number;
  /** Brute: slam damage. Tank: shell damage (the landed-slam rule applies verbatim). */
  slamDamage: number;
  /** Opt in to formation-aware edge attacks for expedition bosses. */
  fairAttackZones?: boolean;
  /**
   * Plan 15 tank fields — all optional; undefined kind keeps the brute (L1, synthetic specs).
   * Plan 19 D1: 'heli' is the tank's mechanical superset — it reuses ALL four aim-cycle fields
   * below and the fire-cycle behaviour verbatim (`bossUsesFireCycle`).
   */
  kind?: 'brute' | 'tank' | 'heli';
  /** Strafe amplitude in x-units: bossStrafeX = strafeHalfWidth · sin(2π t / strafePeriod). */
  strafeHalfWidth?: number;
  /** Strafe period in seconds (L2 4.8). */
  strafePeriod?: number;
  /** Hull hit half-width in x-units: a bullet hits iff |b.x − strafeX| ≤ hullHalfWidth. */
  hullHalfWidth?: number;
  /** Seconds the shell is in flight between bossFire and detonation (≤ slamTelegraph). */
  shellFlight?: number;
}

/**
 * Tank lock band half-width (plan 15 §4, pinned — the render lanes build against this): the locked
 * zone is [squadX ± TANK_ZONE_HALF_X] with the centre clamped to ±(1 − TANK_ZONE_HALF_X), so the
 * band always fits the deck. Matches the SLAM_ZONES centre-band half-width.
 */
export const TANK_ZONE_HALF_X = 0.3;

/**
 * Tank muzzle offset from the hull centre along z (plan 15 §4): NEGATIVE — the muzzle sits ahead
 * of the hull, toward the squad. Authored to a ~3.4 m barrel. bossFire.z = boss.z + this,
 * ABSOLUTE; P15.3 test-pins the rest-pose rendered muzzle world z to exactly this value.
 * Fix round: −3.4 authored × TANK_SCALE 1.45 (tank.ts) = −4.93 — heavy-tank proportions.
 */
export const TANK_MUZZLE_DZ = -4.93;

/**
 * Heli rocket-pod offset from the hull centre along z (plan 19 D1): NEGATIVE — the pods sit just
 * ahead of the fuselage, toward the squad. bossFire.z = boss.z + this, ABSOLUTE. The tank's
 * TANK_MUZZLE_DZ barrel-walk does NOT apply (a heli has no barrel to walk out along): the heli's
 * bossFire.x is the strafe x itself, not an aim-derived muzzle x.
 */
export const HELI_ROCKET_DZ = -1.2;

/**
 * Does this boss spec run the aim-fire cycle (lock → telegraph → fire → detonation)? True for
 * 'tank' and 'heli' (plan 19 D1) — the ONE source for the sim guards (`updateBoss`, `tryHitBoss`,
 * `bossStrafeX`), `validateLevel`'s fire-cycle checks (four fields, deck fit, escape-time) and the
 * render layers, so a heli can never half-enlist: one predicate flips every behaviour together.
 */
export function bossUsesFireCycle(spec: BossSpec): boolean {
  return spec.kind === 'tank' || spec.kind === 'heli';
}

export interface WeaponSpec {
  /** Seconds between shots. */
  fireInterval: number;
  /** Bullets per shot (deterministic lateral spread). */
  bulletsPerShot: number;
  /** Damage per bullet vs boss/crate. */
  damage: number;
  /** Muzzle velocity, m/s. */
  bulletSpeed: number;
  /**
   * 1D-honest splash (plan 04 P1.6): the same-target multiplier on a connected hit. Versus the
   * crate/boss it doubles the per-hit damage (2 × 14 = 28 for the bazooka); versus an enemy block
   * it removes `splash` soldiers per connected shot, bypassing ENEMY_KILL_INTERVAL (the bazooka's
   * anti-block role — small arms keep the suppression interval). Never cross-lane: one connected
   * shot, one target, and never both gates of a pair.
   */
  splash?: number;
  label: string;
}

export interface LevelSpec {
  name: string;
  startN: number;
  /** Forward speed, m/s. */
  speed: number;
  /** Lateral speed in normalized units/s (x ∈ [-1, 1]). */
  lateralSpeed: number;
  gates: GatePairSpec[];
  crates: CrateSpec[];
  enemies: EnemySpec[];
  /** Static hazards (plan 04 P1.4). Plan 15: L1 ships its first two spike strips (design §2). */
  hazards: HazardSpec[];
  /** Shootable obstacles (plan 09). Plan 14: L1 ships the barrel trio (trap + chain pair). */
  obstacles: ObstacleSpec[];
  boss: BossSpec;
  weapons: WeaponSpec[];
}

/** Applied gate operation. Pure. */
export function applyGate(n: number, gate: GateSpec): number {
  if (gate.kind === 'mul') return Math.max(0, Math.floor(n * gate.value));
  if (gate.kind === 'div') return Math.max(0, Math.floor(n / gate.value));
  if (gate.kind === 'rate') return n;
  return Math.max(0, n + gate.value);
}

/** Display string for a gate ("+12", "−20", "×2", "÷3", "RATE+"). */
export function gateLabel(gate: GateSpec): string {
  if (gate.kind === 'mul') return `×${gate.value}`;
  if (gate.kind === 'div') return `÷${gate.value}`;
  if (gate.kind === 'rate') return 'RATE+';
  return gate.value >= 0 ? `+${gate.value}` : `−${Math.abs(gate.value)}`;
}

/** Max shootable value of a numeric gate (cap so late gates stay meaningful). */
export const GATE_VALUE_CAP = 10;

/** Smallest legal ÷ divisor: ÷1 is a no-op gate and ÷0 is undefined (plan 04 P1.3). */
export const MIN_DIVISOR = 2;

/** Smallest legal × multiplier: ×1 is a no-op gate. */
export const MIN_MULTIPLIER = 2;

/**
 * Level-shape validation for shipped levels (plan 04 P1.3) — returns the list of problems, empty
 * means legal. The gate-value rules are the load-bearing ones: a non-integer or < 2 divisor would
 * silently floor a track to 0, and every gate op assumes integers. The track tests call this for
 * every level in SHIPPED_LEVELS.
 */
export function validateLevel(level: LevelSpec): string[] {
  const problems: string[] = [];
  const hazardZs = new Set<number>();
  const bad = (ok: boolean, msg: string): void => {
    if (!ok) problems.push(msg);
  };
  const runEnd = level.boss.z - level.boss.standoff;

  bad(level.weapons.length > 0, 'no weapons');
  for (const w of level.weapons) {
    bad(w.fireInterval > 0, `weapon ${w.label}: fireInterval ${w.fireInterval} <= 0`);
    bad(w.bulletSpeed > 0, `weapon ${w.label}: bulletSpeed ${w.bulletSpeed} <= 0`);
    bad(w.damage > 0, `weapon ${w.label}: damage ${w.damage} <= 0`);
    bad(Number.isInteger(w.bulletsPerShot) && w.bulletsPerShot >= 1 && w.bulletsPerShot <= 3,
      `weapon ${w.label}: bulletsPerShot ${w.bulletsPerShot} outside the 1..3 spread table`);
  }

  bad(level.boss.standoff > 0, 'boss standoff must be > 0');
  bad(level.boss.hp > 0, 'boss hp must be > 0');
  bad(level.boss.slamPeriod > level.boss.slamTelegraph,
    `boss slamTelegraph ${level.boss.slamTelegraph} >= slamPeriod ${level.boss.slamPeriod}`);
  if (level.boss.fairAttackZones) {
    // Include a reaction delay and clearance beyond the inclusive collision boundary.
    const distance = bossUsesFireCycle(level.boss) ? 0.62 : 1.22;
    bad(level.lateralSpeed > 0 && level.boss.slamTelegraph >= 0.25 + distance / level.lateralSpeed,
      'boss fair attack telegraph is too short for a 0.25 s reaction and escape');
  }
  if (bossUsesFireCycle(level.boss)) {
    // Plan 15 §4: a fire-cycle spec must carry ALL FOUR fields, in range — undefined or
    // out-of-range hull/strafe numbers would silently degenerate the hull hit test or the strafe
    // helper. Plan 19 D1: the block runs for the heli UNCHANGED — the messages ride the kind, so
    // a tank reads 'tank boss:' (the tank.test.ts pins' verbatim string) and a heli 'heli boss:'.
    const b = level.boss;
    const tag = `${b.kind} boss:`;
    bad(b.strafeHalfWidth !== undefined && b.strafeHalfWidth >= 0 && b.strafeHalfWidth < 1,
      `${tag} strafeHalfWidth ${b.strafeHalfWidth} must be present and in [0, 1)`);
    bad(b.hullHalfWidth !== undefined && b.hullHalfWidth > 0 && b.hullHalfWidth <= 1,
      `${tag} hullHalfWidth ${b.hullHalfWidth} must be present and in (0, 1]`);
    bad(b.strafePeriod !== undefined && b.strafePeriod > 0,
      `${tag} strafePeriod ${b.strafePeriod} must be present and > 0`);
    bad(b.shellFlight !== undefined && b.shellFlight > 0 && b.shellFlight <= b.slamTelegraph,
      `${tag} shellFlight ${b.shellFlight} must be present and in (0, slamTelegraph ${b.slamTelegraph}]`);
    // The hull interval [strafeX ± hullHalfWidth] never leaves the deck.
    bad(b.strafeHalfWidth !== undefined && b.hullHalfWidth !== undefined &&
      b.strafeHalfWidth + b.hullHalfWidth <= 1,
      `${tag} strafeHalfWidth ${b.strafeHalfWidth} + hullHalfWidth ${b.hullHalfWidth} leaves the deck (must be ≤ 1)`);
    // The TIME rule (the zone-fit inequality 2·TANK_ZONE_HALF_X + 2·SQUAD_FOOTPRINT_HALF_X ≤ 1.8
    // is a static test — it cannot fail per level): the squad must be able to cross the
    // worst-case escape distance inside the lock-to-fire window. Plan 22 note: escapes are now
    // bounded by the on-deck steer range ±steerLimit(halfX(n)), not the rail — from n = 14
    // (halfX ≥ 0.35) a centre-locked shell is UNAVOIDABLE by design, and the brute's wide zones
    // [-1,0.2]/[-0.2,1] from n ≈ 21 (halfX ≥ 0.4) (crowd-tax, owner decisions 1+6). The TIME
    // rule stays conservative for every escape that still exists: the REQUIRED travel is
    // zoneHalf + halfX + margin ≤ 0.3 + 0.55 + 0.05 = 0.9 < escape = 1.15, at every size.
    const escape = 2 * TANK_ZONE_HALF_X + SQUAD_FOOTPRINT_HALF_X;
    // audit #12: the gun FIRES at slamTelegraph − shellFlight, so the squad's real reaction
    // window is the flight-REMOVED span — a 0.6 s lock with a 0.55 s flight leaves 0.05 s, not
    // 0.6 s (the old span overrated it and shipped specs where every shell was unavoidably
    // lethal). `?? 0` keeps shellFlight-less fire-cycle specs validating exactly as before.
    bad(level.lateralSpeed * (b.slamTelegraph - (b.shellFlight ?? 0)) >= escape,
      `${tag} slamTelegraph ${b.slamTelegraph} s × lateralSpeed ${level.lateralSpeed} < ${escape} x-units of worst-case escape — the locked band is not dodgeable`);
  }

  let prevZ = 0;
  for (const pair of level.gates) {
    bad(pair.z > prevZ, `gate pair z=${pair.z} not strictly increasing (previous ${prevZ})`);
    bad(pair.z > 0 && pair.z < runEnd, `gate pair z=${pair.z} outside the run window (0..${runEnd})`);
    prevZ = pair.z;
    for (const side of ['left', 'right'] as const) {
      const gate = pair[side];
      const where = `gate ${side} @z=${pair.z}`;
      bad(Number.isInteger(gate.value), `${where}: value ${gate.value} is not an integer`);
      const other = side === 'left' ? pair.right : pair.left;
      if (gate.kind === 'rate') {
        // Plan 11: one pip per gate, traded against soldiers — never a −k/÷ (a free pick) or
        // another upgrade (a false choice).
        bad(gate.value === 1, `${where}: rate gate value ${gate.value} must be 1`);
        bad(other.kind === 'num' && other.value > 0, `${where}: rate gate partner must be a +k gate`);
      } else if (gate.kind === 'div') {
        bad(gate.value >= MIN_DIVISOR, `${where}: divisor ${gate.value} < ${MIN_DIVISOR}`);
      } else if (gate.kind === 'mul') {
        bad(gate.value >= MIN_MULTIPLIER, `${where}: multiplier ${gate.value} < ${MIN_MULTIPLIER}`);
      } else {
        bad(gate.value !== 0, `${where}: a +0 gate is a no-op`);
      }
    }
    // Shooting lifts any +k below the cap to the cap, so two such gates end as the same +10: a
    // num/num pair needs one side printed above the cap to stay two different choices.
    if (pair.left.kind === 'num' && pair.right.kind === 'num') {
      bad(Math.max(pair.left.value, pair.right.value) > GATE_VALUE_CAP,
        `gate pair z=${pair.z}: both sides shoot up to the same +${GATE_VALUE_CAP}`);
    }
  }

  for (const crate of level.crates) {
    bad(crate.z > 0 && crate.z < runEnd, `crate z=${crate.z} outside the run window (0..${runEnd})`);
    bad(crate.hp > 0, `crate z=${crate.z}: hp ${crate.hp} <= 0`);
    bad(Math.abs(crate.x) <= 1, `crate z=${crate.z}: x ${crate.x} outside [-1,1]`);
  }
  for (const block of level.enemies) {
    bad(block.dropReward === undefined || typeof block.dropReward === 'boolean',
      `enemy block z=${block.z}: dropReward must be boolean`);
    bad(block.killInterval === undefined || (Number.isFinite(block.killInterval) && block.killInterval >= 0),
      `enemy block z=${block.z}: killInterval must be finite and nonnegative`);
    bad(block.z > 0 && block.z < runEnd,
      `enemy block z=${block.z} outside the run window (0..${runEnd})`);
    bad(Number.isInteger(block.count) && block.count > 0,
      `enemy block z=${block.z}: count ${block.count} invalid`);
    bad(block.halfWidth > 0 && block.halfWidth <= 1,
      `enemy block z=${block.z}: halfWidth ${block.halfWidth} invalid`);
    bad(Math.abs(block.x) + block.halfWidth <= 1,
      `enemy block z=${block.z}: x=${block.x} halfWidth=${block.halfWidth} leaves no lane`);
    if (block.dropReward !== false) bad(block.z + PICKUP_AHEAD < runEnd, `enemy block z=${block.z}: its pickup lands past the run end`);
  }
  for (const hazard of level.hazards) {
    // z is the key every hazard consumer looks up by (the sim's latch scan, the render props, the
    // probe, the dodgeability property) — two bands on one z would alias into one there, so the
    // second becomes invisible to the latch and unrenderable while still costing soldiers.
    bad(!hazardZs.has(hazard.z), `hazard z=${hazard.z}: duplicate z (z-keyed lookups would alias)`);
    hazardZs.add(hazard.z);
    bad(hazard.z > 0 && hazard.z < runEnd,
      `hazard z=${hazard.z} outside the run window (0..${runEnd})`);
    bad(Number.isInteger(hazard.penalty) && hazard.penalty > 0,
      `hazard z=${hazard.z}: penalty ${hazard.penalty} must be a positive integer`);
    bad(hazard.halfWidth > 0 && hazard.halfWidth <= 1,
      `hazard z=${hazard.z}: halfWidth ${hazard.halfWidth} invalid`);
    bad(Math.abs(hazard.x) <= 1, `hazard z=${hazard.z}: x ${hazard.x} outside [-1,1]`);
    // The P1.4 dodgeability precondition: at least one safe centremost-x must survive the clamp.
    bad(hazardSafeRanges(hazard).length > 0,
      `hazard z=${hazard.z}: threatens the whole deck (no safe x within [-1,1])`);
  }
  for (const o of level.obstacles) {
    const where = `obstacle ${o.kind} @z=${o.z}`;
    bad(['barrel', 'barricade'].includes(o.kind), `${where}: unknown kind`);
    bad(o.z > 0 && o.z < runEnd, `${where} outside the run window (0..${runEnd})`);
    bad(Math.abs(o.x) <= 1, `${where}: x ${o.x} outside [-1,1]`);
    bad(o.halfWidth > 0 && o.halfWidth <= 1, `${where}: halfWidth ${o.halfWidth} invalid`);
    bad(Number.isInteger(o.hp) && o.hp > 0, `${where}: hp ${o.hp} must be a positive integer`);
    // Same dodgeability precondition as a hazard: the squad can always step around it.
    bad(hazardSafeRanges({ z: o.z, x: o.x, halfWidth: o.halfWidth, penalty: 1 }).length > 0,
      `${where}: blocks the whole deck (no safe x within [-1,1])`);
  }
  return problems;
}

/** Seconds between enemy kills while a block is under fire (dug-in cover: fire softens, never deletes). */
export const ENEMY_KILL_INTERVAL = 0.25;

/**
 * Metres ahead of the squad within which an enemy block is accounted for (plan 04 P1.5). One
 * number for both consumers: the HUD's "M" readout and the novice bot's lookahead (P1.1) use the
 * same window, so what the player is told to expect is what the bot reacts to.
 */
export const ENEMY_LOOKAHEAD = 15;

export const WEAPONS: WeaponSpec[] = [
  { fireInterval: 0.18, bulletsPerShot: 1, damage: 2, bulletSpeed: 70, label: 'SIDEARM MK I' },
  { fireInterval: 0.14, bulletsPerShot: 2, damage: 2, bulletSpeed: 70, label: 'RIFLE MK II' },
  { fireInterval: 0.11, bulletsPerShot: 3, damage: 2, bulletSpeed: 70, label: 'MINIGUN MK III' },
  // Splash doubles per-hit damage vs crate/boss: 2 × 14 = 28 per hit at 0.35 s ⇒ 80 single-target
  // DPS, clear of the minigun's 54.5 (without the doubling the spec would sit at 40 < 54.5).
  { fireInterval: 0.35, bulletsPerShot: 1, damage: 14, bulletSpeed: 50, splash: 2, label: 'BAZOOKA MK IV' },
];

/**
 * Level 1 — "Operation Highline" (handcrafted, ~60 s: 46 s run + boss).
 *
 * Plan 12 redesign (owner request 2026-10-19): nine pairs ~45 m apart (was six, ~70 m), and every
 * pair is two DIFFERENT choices after shooting (`validateLevel`: a num/num pair keeps one side above
 * the cap — the old −20|−25 pair ended as +10|+10). Pair roles: real choices (×2|+40 and ×2|+90,
 * where the better side depends on N — greedy's × is the trap at the first), plain bigger-number
 * picks (+15|+4, +25|+12, +30|+12), and bad choices (÷2 three times, −30) that only cost you.
 *
 * Plan 13: the two enemy blocks hold ONE lane each (280 left, 430 right, at ±0.6 so the centre-
 * holding probe run's guns miss them). Contact needs footprint overlap, so a block is a choice: steer
 * into its lane and wipe it out for a firepower pickup 8 m past it, or walk past in the other lane.
 * Plan 14 adds the barrel trio (see the `obstacles` block below for each lesson's geometry).
 *
 * Audit trail — MEASURED (`policies.test.ts` / `sim.test.ts` / `face-tank.test.ts` pin FLOORS, not
 * these figures; re-measured with the plan-15 additions). Smart: 10 → +15 = 25 → +40 = 65 → +20 =
 * 85 → +25 = 110 → crate (Rifle Mk II) → ×2 = 220 → +20 = 240 → block 280 wiped → pickup (Minigun
 * Mk III) → +30 = 270 → +25 = 295 → [hazard 310 dodged: it is mid-transit to the z-335 left lane
 * and crosses at ≈ −0.6, outside the band's [−0.4, 1.3] threat] → block 345 walked past (Δ 1.15,
 * no contact, not wiped) → +40 = 335 → barrels 398/403 popped from range by its left-lane fire →
 * block 408 wiped → pickup (Bazooka Mk IV) → block 430 walked past → **335** on TIER 3, 0 hazard
 * hits, 0 obstacle bills, 1 slam (dodged) → WIN at 52.1 s (5.9 s fight at 1730). Novice: same
 * gates, no crate, EATS A (−20 at z 222), dodges BOTH new hazards to the rails (0 hazard hits),
 * pops B/C + 398/403 from its held-lane fire, wipes 345 for its pickup → WIN **315** on TIER 2 at
 * 54.4 s. Greedy (always ×, else the bigger printed number): ×2 at 25 = 50 → 70 → 95 → ×2 = 190 →
 * [A contact detonation −20 — it transits to the z-235 RIGHT lane across A's body] 170 → +20 =
 * 190 → +30 = 220 → +25 = 245 → [hazard 362 −20: it holds the LEFT lane from z 335 through the
 * end, straight through the band's [−1.3, 0.4] threat] 225 → +40 = 265; 398/403 pop from its held
 * left-lane fire (no bill), block 408 wiped by the same fire → pickup ⇒ TIER 1 → arrives with
 * **265** on TIER 1 ⇒ 113.6 measured DPS (4 columns, brute never misses). At hp 1730 it now WINS
 * (4 slams × 60 = 240 < 265) — the plan-15 additions moved its arrival from tier 0/N 285 to
 * tier 1/N 265, so the boss rules re-bisected (floors may only rise):
 *
 * Boss hp (re-bisected 2026-10-19, plan-15 P15.1 audit, over hp on the real sim):
 *  (a) greedy (N 265, tier 1, 0 pips, 113.6 DPS) loses from 1809;
 *  (b′) stand-still at greedy's arrival loses from 1897;
 *  (e) the hazard/obstacle-dodging greedy twin still loses from 2021;
 *  the repo-pinned one-slam of ARITHMETIC slack (floor(hp / DPS / 3.5) × 60 > 265 + 60, asserted
 *  in face-tank.test.ts) binds at 6 slams ⇒ ≥ 2386 (dps ≤ the 114.3 spec: 2400 gives exactly 6);
 *  the channel-(a) weaker-boss slack (hp − one slam of DPS still loses) ⇒ ≥ 2207. ((d) is vacuous:
 *  L1 ships no RATE+ pairs.) ⇒ **2400** (was 1730). Smart fight 8.8 s, novice 11.9 s (total ≈ 58 s).
 */
export const LEVEL_1: LevelSpec = {
  name: 'Operation Highline',
  startN: 10,
  speed: 10,
  lateralSpeed: 2.2,
  gates: [
    // The first pair stays at z=18: the squad grows inside 1.8 s (plan 04 P1.9 ad pacing).
    { z: 18, left: { kind: 'num', value: 15 }, right: { kind: 'num', value: 4 } },
    { z: 58, left: { kind: 'mul', value: 2 }, right: { kind: 'num', value: 40 } },
    { z: 98, left: { kind: 'num', value: 20 }, right: { kind: 'div', value: 2 } },
    // +25 on the left keeps greedy off the crate lane (it arrives on tier 0).
    { z: 140, left: { kind: 'num', value: 25 }, right: { kind: 'num', value: 12 } },
    { z: 200, left: { kind: 'mul', value: 2 }, right: { kind: 'num', value: 90 } },
    { z: 235, left: { kind: 'div', value: 2 }, right: { kind: 'num', value: 20 } },
    { z: 295, left: { kind: 'num', value: 12 }, right: { kind: 'num', value: 30 } },
    { z: 335, left: { kind: 'num', value: 25 }, right: { kind: 'div', value: 2 } },
    { z: 375, left: { kind: 'num', value: 40 }, right: { kind: 'num', value: -30 } },
  ],
  crates: [{ z: 170, hp: 6, x: 0.35 }],
  enemies: [
    { z: 280, count: 15, x: -0.6, halfWidth: 0.35 },
    // Plan 15 additions (design §2, audit-final): lane-held blocks with pickups at 353/416.
    { z: 345, count: 10, x: 0.55, halfWidth: 0.3 },
    { z: 408, count: 12, x: -0.55, halfWidth: 0.3 },
    { z: 430, count: 12, x: 0.6, halfWidth: 0.35 },
  ],
  // Plan 15: L1's first hazards — spike strips the centre-holding runs pay for (design §2).
  hazards: [
    { z: 310, x: 0.45, halfWidth: 0.3, penalty: 15 },
    { z: 362, x: -0.45, halfWidth: 0.3, penalty: 20 },
  ],
  obstacles: [
    // Plan 14 trio (design §4), measured against the real constants (STREAM_PITCH 0.1 ×(±1.5) =
    // ±0.15 column offsets + ±0.09 intra-volley spread ⇒ gun-line catch = |streamX − x| ≤ 0.15,
    // worst-tier reach 0.15 + 0.09 + 0.15 = 0.39 from the lane centre; contact = |squadX − x| ≤
    // 0.15 + 0.55 footprint = 0.70; BLAST_REACH_Z 6; blocks are point-z):
    // A — lane-holder trap: a SETTLED right-lane holder (x = 0.6) sits Δ 0.45 ∈ (0.39, 0.70]
    //     from it — inside body width, 0.06 off even the spread-tier gun line (nearest settled
    //     stream 0.45 > window edge 0.30), so nothing in the held lane shoots it and the contact
    //     detonation bills −20. Left-lane holders clear it (Δ 0.75 > 0.70). Measured (P14.1
    //     audit): greedy's z 200–206 lane-cross volley sweep only chips A to 10 hp (16 hp needs 8
    //     tier-0 hits; the sweep lands 3), so its contact detonation −20 LANDS; the novice eats
    //     the same −20 (its z-235 escape target is on A's far side and fires too late); smart
    //     escapes to x 0.87 and clears it.
    { z: 222, x: 0.15, halfWidth: 0.15, hp: 16, kind: 'barrel' },
    // B + C — chain pair by block 280 (x −0.6, span [−0.95, −0.25]): B→C Δz 5 ≤ 6 chains (B's
    //     blast [−0.9, −0.1] ⊇ C's span [−0.7, −0.4], damage 20 ≥ hp 16), and C→block Δz 5 ≤ 6:
    //     C's blast [−0.95, −0.15] overlaps the block's span ⇒ enemyKill 10 softens 15 → 5. B
    //     sits on the approach into the block lane (smart steers to −0.6 to wipe the block for
    //     its pickup; its −0.65/−0.55/−0.45 streams are inside B's [−0.65, −0.35] window ⇒ shot
    //     from range, both pop far ahead of the squad, no blast bill — the novice's held left-
    //     lane fire and greedy's z 200–206 lane-cross sweep pop them the same way). Greedy passes
    //     in the right lane (Δ ≥ 1.1): no contact.
    { z: 270, x: -0.5, halfWidth: 0.15, hp: 16, kind: 'barrel' },
    { z: 275, x: -0.55, halfWidth: 0.15, hp: 16, kind: 'barrel' },
    // Plan 15 chain pair into block 408 (design §2): Δz 5 ≤ 6 chains, and C'403's blast reaches
    // the block (RELATIVE bound: 408 − 403 = 5 ≤ 6, so a block nudge keeps the chain). Both sit
    // in the block lane — the bots that steer into −0.55 pop them from range; a settled right-
    // lane holder (x 0.6, Δ ≥ 1.1) never contacts or shoots them.
    { z: 398, x: -0.5, halfWidth: 0.15, hp: 16, kind: 'barrel' },
    { z: 403, x: -0.55, halfWidth: 0.15, hp: 16, kind: 'barrel' },
  ],
  boss: { z: 480, hp: 2400, standoff: 18, slamPeriod: 3.5, slamTelegraph: 1.3, slamDamage: 60 },
  weapons: WEAPONS,
};

/**
 * Level 2 — "Operation Scrapline" (plan 04 P1.7, handcrafted; plan 19 D4 multiplier reduction —
 * 45 s run + boss ≈ 51 s smart play / 54 s novice).
 *
 * What L1 could not carry (its layout is frozen except the plan-14 barrel trio):
 *  - **÷ gates** (5 pairs), including the printed-inversion pair at z=420 (`÷3` vs `÷2` — the
 *    ad-viewer reads ÷3 as the bigger number and floors its own squad harder, on the way to the
 *    boss; placing it last is what makes its absolute cost the largest on the track);
 *  - **4 static hazards** (P1.4) interleaved between the gate pairs — a CONTRIBUTORY channel (b)
 *    for the greedy policy, not the lethal one: it holds its lane through all four bands and pays
 *    70 soldiers for it, and the counterfactual in `level2.test.ts` ("the hazard channel is
 *    material", `dodgeImminentHazards`) runs the same ad brain with every band (and, since plan
 *    09, every live obstacle) stepped around — 0 hazard hits, arrival 112 instead of 69 (plan-19
 *    D4 re-measure) — and it still LOSES. See the greedy audit line;
 *  - **3 crates** → the weapon ladder to tier 3 (BAZOOKA MK IV, P1.6). L1 stops at tier 1.
 *  - **lane-held enemy blocks** (M=12 at z=240, M=20 at z=360) instead of L1's road-blocking
 *    formation: a block you must steer into to soften, and — load-bearing for the gate tooling —
 *    one the centre-holding `?debug=1` probe run never suppresses, so the pre-contact shots always
 *    have a headcount on the HUD (`tools/inspect.mjs` asserts displayed M == sim count).
 *  - **2 RATE+ weapon pairs** (plan 11) at z=42 and z=322: a rate pip (+25 % fire rate, stripped
 *    by a landed slam) against a `+k`, i.e. soldiers vs a shorter boss fight. PLAN 19 D4 NOTE: with
 *    the ×3 gone the trade is strictly dominated in N and neutral in time (both branches cap at
 *    the 2-pip block-pickup ceiling), so the RATE+ gates became "wrong-looking choice" gates —
 *    same class as the ÷ gates. OWNER SIGN-OFF 2026-10-20 (plan-gate r1): they STAY as trap gates
 *    — removing them would change more frozen L2 data than the owner asked for. See the rule-(d)
 *    note in the boss tuning below and the level2.test.ts re-pin.
 *  - **4 barrels + 1 barricade** (plan 09, stats plan 14): trap channel (e) "obstacle contact",
 *    contributory like (b). Barrel #0 (z 195) sits inside the z-180 multiplier lane-holder's body
 *    width but off its gun line (at halfWidth 0.15 the [−0.35, −0.05] window misses the settled
 *    −0.45 columns by 0.10) — greedy pays its −20 blast, re-measured plan 19. Barrel #4 (z 355) is
 *    the plan-14 lesson flip: its [0.15, 0.45] window CATCHES the 0.45 column, so greedy pops it
 *    at range instead of walking into it, and the blast takes 10 off block 360. The 233/236 pair
 *    chains (#2's blast takes 10 off block 240); the plan-15-era greedy left it standing (the
 *    z-210 +25 gate ate its left-lane bullets), the plan-19 greedy — whose lanes the gate
 *    reduction moved — pops both with the crossing sweep. The dodging bots shoot every barrel from
 *    range. The barricade is breached in time by every named bot under plan 10's columns (accepted).
 *
 * Audit trail — MEASURED (plan-19 D4 re-measure on the REDUCED gates — ×2|+14 @60 → +8|+14,
 * ×3|+40 @180 → ×2|+40; the plan-15/plan-14 figures are compressed history, kept per this repo's
 * audit-comment style. `level2.test.ts` pins FLOORS over these numbers — smart ≥ 150, novice ≥
 * 100, greedy `lost` with ≥ 2 hazard hits (measured 4) and ≥ 1 landed shell (measured 1), both
 * weapon branches win for every k, plus.n > rate.n — it does not assert the figures themselves,
 * so a re-tune can move them without a red test). Smart play (gates shot while it holds the lane,
 * its fire wipes 240/360/398 — every hazard, obstacle and shell dodged, all three crates): 12 →
 * +12 = 24 → +6 (shot to its +10 cap) = 34 → +14 = 48 → +30 = 78 → ×2 = 156 → +25 = 181 → +30 =
 * 211 → +50 = 261 → +20 = 281 → +60 = 341 → ÷2 = **170** on TIER 3 with 2 pips (the ladder tops
 * out at crate 250, block 240's wipe grants tier 3, pickups 360/398 become the pips) @ 45.2 s
 * arrival; 168.0 measured LANDED DPS at its arrival state (tier 3, 2 pips, deck centre) → 0/1
 * shells landed → **WIN 170 at 50.7 s** (5.5 s fight at the pinned 841), 0 hazard hits, 0
 * obstacle bills. Novice bot: the same picks, the same arrival — **170**, TIER 3, 2 pips → **WIN
 * at 54.2 s** (9.0 s fight), 0 hazard hits, 0 obstacle losses, 0/2 shells landed. Greedy
 * (printed-score ad brain, never steers; RATE+ is a word, so it takes the `+k`): 24 → +6 (its
 * lane fire caps it) = 34 → +14 = 48 (the reduced num/num pair — no ×) → [−15 @110] 33 → +30 =
 * 63 → [−15 @170] 48 → ×2 = 96 → [barrel #0: the crossing sweep pops it, the blast −20] 76 → +25
 * = 101 → [233/236 popped by the same sweep; block 240 thinned, contact free] → +30 = 131 → [−20
 * @288] 111 → +50 = 161 → +20 = 181 → [#4 popped at range; its blast + greedy's fire wipe block
 * 360 → pickup ⇒ TIER 2] → [−20 @348] 161 → +60 = 221 → [block 398 contact −12 (its ÷3-lane
 * transit clips the block)] 209 → [437: the pre-÷3 volley breaches it before contact — NO wall
 * bill; the plan-15 era measured −5] → **÷3** = **69** on TIER 2, 0 pips @ −0.6 ⇒ 55.6 LANDED DPS
 * (measured at deck centre — lower at its hold, the conservative direction; the hull misses most
 * of its left-lane columns) ⇒ 1 shell fired, 1 landed ([−0.9, −0.3], zone locked on its hold lane)
 * −70 → **LOST at N=0, t = 48.7 s** (3.5 s fight). The loss is SHELL-DECIDED with a ONE-SHELL
 * MARGIN — 69 < the 70 shell, so the SINGLE landed shell ends it (the plan-15 era measured 2
 * landed shells at N 100 — channel (a) alone is decisive; (b) the hazard bill (−70, all four
 * bands, penalty exactly one shell's worth), (c) the ÷3 inversion and (e) the −20/−12 obstacle
 * bills only lower the N it arrives with). Its hazard/obstacle-dodging twin arrives 112/t3 and
 * STILL loses (2/2 shells landed) — rule (e).
 *
 * Boss DATA tuning (the plan-10 bisection procedure re-run over hp on the REAL SIM at the reduced
 * gates, 2026-10-20; the losing rules bind at the squads the non-dodging bots ACTUALLY bring):
 *  (a) greedy (69, tier 2, 0 pips, 55.6 landed DPS) loses from 231 hp; one shell of its landed
 *      DPS of slack ⇒ ≥ 346; the weaker-boss channel-(a) slack ⇒ ≥ ~235;
 *  (b′) stand-still at greedy's arrival state (69, tier 2, 0 pips) loses from 235;
 *  (e) the hazard/obstacle-dodging greedy twin must still lose: from **729** — the BINDING rule
 *      now (it WINS at 700: the reduced gates leave the twin smaller than the plan-15 twin);
 *  fight floor: smart's fight reaches 5 s from ≈ 600 hp and is 5.5 s at 841 (≤ the 8 s cap);
 *  ceiling: smart AND novice still win with margin (n = 170 ≥ 100) up to hp 5656 (smart 103 s,
 *  novice 84 s — the cross-level suite's t < 120 binds first at ≈ 5000).
 *  ⇒ window [729, ≥ 5656]; **841 KEPT** (plan 19 D4: the owner ask was the multipliers, hp was
 *  not re-pinned to the new floor). Rule (d), the plan-15 BINDING rule at 841 ("no dominant
 *  weapon pick" over the imperfect dodgers k = 2..6), is STRUCTURALLY GONE with the ×3: the
 *  trade's payoff compounded through the ×2/×3 multipliers (plan 11), and both branches now cap
 *  at the 2-pip block-pickup ceiling, so RATE+ ends 170 → 150 N for BOTH bots and is time-neutral
 *  (smart 51.0 vs 50.7 s, novice 54.2 vs 54.2 s) — reports/P19-0 §fix-3. The test re-pins the
 *  dominated direction instead (both branches win for every k; plus.n > rate.n) so a future
 *  re-tune that RE-OPENS the trade flips it and forces a conscious re-pin. Plan-15 history
 *  (tank-mechanic re-baseline on the unreduced gates): (a) 231, (b′) 225, (d) 841 BINDING, (e) 85,
 *  fight floor 533, ceiling 5656, window [841, 5656] → win 239, greedy N 100/t1, 2 landed shells.
 * The face-tank invariant holds with the same shape as the brute's. Fight length at the pin:
 * smart 5.5 s, novice 9.0 s. N range over the reaching paths: 1..170 (all 2048 lane-hold paths +
 * the three named policies reach the boss).
 */
export const LEVEL_2: LevelSpec = {
  name: 'Operation Scrapline',
  startN: 12,
  speed: 10,
  lateralSpeed: 2.2,
  gates: [
    // Opening pair. Six metres ahead of the l2_z20_early inspect pause rather than two: the pair is
    // hidden once consumed (Gates disables consumed gates), so the shot has to stop short of it.
    // That margin used to absorb the frame-granular pause trigger (~4 m of overshoot); the pause is
    // checked per SUBSTEP now (plan 04 fix round), so overshoot is one SIM_DT ≈ 0.083 m and the 6 m
    // is the shot's framing distance, not slop headroom.
    // +12|+4, not +8|+3 (plan 12): both of those shot up to the same +10. Greedy's run and the
    // boss bisection below are unchanged by it (re-measured); smart gains 6.
    { z: 26, left: { kind: 'num', value: 12 }, right: { kind: 'num', value: 4 } },
    // Plan 11 weapon pairs: soldiers vs a rate pip. The early bet — +6 compounds through the ×2
    // (until plan 19 D4 it compounded through ×2/×3), RATE+ speeds every later gate tick, crate and
    // the boss. Plan-19: the bet is now dominated (both branches cap at 2 pips) — trap-gate status,
    // owner sign-off 2026-10-20, see the audit trail.
    { z: 42, left: { kind: 'num', value: 6 }, right: { kind: 'rate', value: 1 } },
    // Plan 19 D4 (owner: progressive difficulty scale): was ×2|+14. L2 keeps exactly ONE
    // multiplier — the ×2 at z=180 — and this pair became a num/num bigger-number pick.
    { z: 60, left: { kind: 'num', value: 8 }, right: { kind: 'num', value: 14 } },
    { z: 140, left: { kind: 'num', value: 30 }, right: { kind: 'num', value: -12 } },
    // Plan 19 D4: was ×3|+40 (the level's only remaining multiplier, ×2).
    { z: 180, left: { kind: 'mul', value: 2 }, right: { kind: 'num', value: 40 } },
    { z: 210, left: { kind: 'num', value: 25 }, right: { kind: 'div', value: 2 } },
    { z: 260, left: { kind: 'div', value: 2 }, right: { kind: 'num', value: 30 } },
    { z: 300, left: { kind: 'div', value: 2 }, right: { kind: 'num', value: 50 } },
    // The late bet, with the fight 13 s away (at z=225 it pulled the novice off the z=250 crate).
    { z: 322, left: { kind: 'rate', value: 1 }, right: { kind: 'num', value: 20 } },
    { z: 390, left: { kind: 'div', value: 2 }, right: { kind: 'num', value: 60 } },
    // Last pair = the printed-inversion trap (channel c), placed where its absolute cost is largest:
    // ÷3 prints bigger than ÷2, so the ad-brain divides its own squad harder, on the way to the boss.
    { z: 420, left: { kind: 'div', value: 3 }, right: { kind: 'div', value: 2 } },
  ],
  crates: [
    { z: 95, hp: 6, x: 0.6 },
    { z: 250, hp: 8, x: -0.6 },
    { z: 380, hp: 10, x: -0.5 },
  ],
  enemies: [
    // Dug-in blocks in ONE lane (not the L1 road-blocking formation): the centre-holding probe run
    // never softens them, so the pre-contact shots always have a headcount to display, and the
    // scorekeeping bots have to steer into the lane to suppress them at all.
    { z: 240, count: 12, x: -0.5, halfWidth: 0.45 },
    { z: 360, count: 20, x: 0.5, halfWidth: 0.45 },
    // Plan 15 addition (design §3, audit-final): pickup at 406; nudge cap z ≤ 411 keeps the pickup
    // clear of gate 420's step.
    { z: 398, count: 14, x: -0.45, halfWidth: 0.4 },
  ],
  hazards: [
    { z: 110, x: -0.55, halfWidth: 0.3, penalty: 15 },
    { z: 170, x: -0.45, halfWidth: 0.28, penalty: 15 },
    { z: 288, x: 0.55, halfWidth: 0.3, penalty: 20 },
    { z: 348, x: 0.45, halfWidth: 0.32, penalty: 20 },
  ],
  obstacles: [
    // #0: 0.4 off the z-180 multiplier lane centre — inside body width (≤ 0.65), off the 4-column
    // gun line (> 0.34). Plan 14: halfWidth 0.15 / hp 16 — greedy's settled −0.45..−0.75 tier-0
    // columns still miss the [−0.35, −0.05] window (0.10 clear), so the −20 bill stays its own
    // (plan-19 re-measure: the crossing sweep pops it and the blast still catches the body).
    { z: 195, x: -0.2, halfWidth: 0.15, hp: 16, kind: 'barrel' },
    // #1 + #2: shooting #1 sets off #2, whose blast (Δz 4) reaches block 240.
    { z: 233, x: -0.5, halfWidth: 0.15, hp: 16, kind: 'barrel' },
    { z: 236, x: -0.2, halfWidth: 0.15, hp: 16, kind: 'barrel' },
    // 15 m behind the z=260 +30 gate: a shoot-or-steer wall (every named bot breaches it in time).
    { z: 275, x: 0.6, halfWidth: 0.35, hp: 28, kind: 'barricade' },
    // #4: shielded for x ≥ 0 by the z=322 RATE+|+20 gate until squad z 320; blast reaches block 360.
    // Plan 14: the [0.15, 0.45] window now catches the 0.45 stream, so greedy pops it at range
    // (its contact bill becomes a ranged detonation whose blast takes 10 off block 360).
    { z: 355, x: 0.3, halfWidth: 0.15, hp: 16, kind: 'barrel' },
    // Plan 15: the last squeeze before the standoff (design §3) — a centre band, the TIGHTEST
    // legal dodge (safe centre intervals only (0.85, 1] in magnitude at the cap). Every named bot
    // breaches it in time or dodges (measured, P15.1 audit); the plan-15-era greedy walked in and
    // paid the −5 wall bill, the plan-19 greedy's pre-÷3 volley breaches it before contact (lost 0).
    { z: 437, x: 0.0, halfWidth: 0.3, hp: 28, kind: 'barricade' },
  ],
  // Plan 15: the boss is a TANK (design §4) — the slam numbers are reused as the fire cycle's
  // cadence/lock-window/shell-damage UNCHANGED; the strafe/hull/shell fields are new. hp 841 KEPT
  // by plan 19 D4 (the owner ask was the multipliers only) — the bisection re-run on the reduced
  // level moved the BINDING rule from (d) to (e): window [729, ≥ 5656], see the audit trail above.
  boss: {
    z: 470, hp: 841, standoff: 18, slamPeriod: 3.5, slamTelegraph: 1.3, slamDamage: 70,
    kind: 'tank', strafeHalfWidth: 0.55, strafePeriod: 4.8, hullHalfWidth: 0.28, shellFlight: 0.35,
  },
  weapons: WEAPONS,
};

/**
 * Level 3 — "Operation Duskline" (plan 19 D2, the finale: handcrafted, ~67 s smart play + the
 * first 'heli' boss). The progressive-scale ask (owner 2026-10-20: "very hard (low multipliers)"):
 * L3 ships NO × gates at all — the finale DIVIDES (the ÷3|÷2 printed-inversion pair sits at z=405,
 * greedy takes ÷3 there exactly like L2's 420) — five hazard bands (three centre squeezes), a
 * size-selective barrel trap, and the heli (plan 19 D1: the tank's fire cycle VERBATIM, every
 * number harder — cadence 3.5 → 2.8 s, lock window 1.3 → 1.0 s, shell 70 → 80, hull 0.28 → 0.24,
 * strafe 0.55/4.8 → 0.65/3.6). The escape-time check (audit #12: the window is lock − flight)
 * holds at the tightest shipped margin: 2.2 × (1.0 − 0.35) = 1.43 ≥ 1.15 x-units (1.24× — the
 * tank's is 2.2 × (1.3 − 0.35) = 2.09, 1.82×).
 *
 * Audit trail — MEASURED (Phase 2 scratch harness, reports/P19-2-audit.md; `level3.test.ts`,
 * `face-tank.test.ts` and the cross-level suite pin FLOORS, not these figures). The D2 tuning
 * mandate CLOSED: the draft's band @245 was the one hazard hit smart ever ate (the block-250
 * pickup pull fought the centre escape); the element was nudged z 245 → 235 (inside the ±15 m
 * budget, plan D2's first candidate) — smart now clears all five bands and every OTHER measured
 * run is unchanged. Nothing was dropped.
 *
 * Smart play (gates shot while it holds the lane, all three crates, every hazard/obstacle/shell
 * dodged): 12 → +14 = 26 → +8 (shot to its +10 cap) = 36 → +12 = 48 → +16 = 64 → +22 = 86 → +25 =
 * 111 → +28 = 139 → +40 = 179 → +36 = 215 → +16 = 231 (skips both RATE+) → ÷2 = 115 → +42 =
 * **157** on TIER 3, 0 pips — barrels 100/118/205/350 popped from range, the 425 wall breached
 * under fire, all three blocks crossed with lost 0 → **WIN at 66.7 s** (21.5 s fight, 0/7 shells
 * landed; 81.2 measured LANDED DPS at its arrival size — the 0.24 hull). Novice bot: same picks,
 * TIER 1 (only crate 200 sits on its lanes), eats block-370 contact −15 (the +16 gate @360 shields
 * it from right-lane fire until the crossing — the L2 barrel-#0 shielding pattern) and the 425
 * wall bill −14 → **WIN 136 at 75.3 s** (30.0 s fight), 0 hazard hits, 0/10 shells landed. Greedy
 * (printed-score ad brain, never steers; RATE+ is a word, so it takes the `+k`): 26 → +8 = 36 →
 * +12 = 48 → [−18 @88] 30 → +16 = 46 → [barrel-118 blast −20 — the size-selective trap: the
 * crossing columns pop it and the blast takes its body width, the settled holds never] 26 →
 * [block-125 contact −10] 16 → +22 = 38 → [−18 @160] 20 → +25 = 45 → crate 200 ⇒ TIER 2 → +28 =
 * 73 → [−20 @235] 53 → +40 = 93 → +36 = 129 → [−20 @330] 109 → +16 = 125 → [−22 @385, its
 * ÷3-lane hold] 103 → **÷3** = 34 → +42 = **76** on TIER 2 @ 48.0 s ⇒ 43.4 LANDED DPS ⇒ **LOST**,
 * ONE landed rocket (80 > 76 — the loss is shell-decided with a one-shell margin; the five bands
 * −98, the −20 blast and the −10 contact only lowered its arrival). Its hazard/obstacle-dodging
 * twin arrives at 115/t2 and STILL loses (2/2 shells landed); stand-still at greedy's arrival
 * loses too. The corrected-misser sweep (P19-0 §fix-2 — cycles counted by observed detonations,
 * hold x 0 through telegraph AND inFlight): both weapon branches win for EVERY imperfect dodger
 * from k = 8 (measured k = 2..8 table in level3.test.ts / reports/P19-2-audit.md — the NOVICE +k
 * branch is the binding curve, eating a shell through k = 7; smart is green from k = 5; at k = 2
 * all four runs eat shells, so the misser is real), and the natural-run RATE trade is LIVE (no
 * dominant pick: smart +k ends bigger 157 vs 144, novice ends bigger on RATE 144 vs 136; RATE
 * fights strictly shorter for both, 21.5 → 16.0 s and 30.0 → 10.6 s). See level3.test.ts.
 *
 * Boss DATA tuning (plan-10 bisection over hp on the real sim, the rules as they apply WITHOUT
 * the retired rule (d) — L2's canary does not port; the live L3 trade is pinned instead):
 *  (a) greedy (76, tier 2, 0 pips, 43.4 landed DPS) loses at EVERY hp measured in [200, 3200];
 *  (b′) stand-still at greedy's arrival state (76, tier 2, 0 pips) loses at the pin;
 *  (e) the dodging greedy twin must still lose: it does from hp 400 (wins at 200 — the floor);
 *  fight floor: smart's fight is 21.5 s at the pin (≥ 5 s; ≈ 11 s already at hp 700);
 *  ceiling: smart AND novice still win with margin (n ≥ 100) past hp 2800 — measured to 3200
 *  (smart 157 at 117 s, novice 136 at 154 s).
 *  ⇒ window [≈400, ≥3200]; **1000** (the plan-19 D2 pin: shell 80 > greedy's 76 arrival, ≥ 3 full
 *  strafe cycles of fight at smart's landed DPS, every rule green with margin). N range over the
 *  reaching paths: 1..157 (3731 of the 4096 lane-hold paths reach the boss).
 */
export const LEVEL_3: LevelSpec = {
  name: 'Operation Duskline',
  startN: 12,
  speed: 10,
  lateralSpeed: 2.2,
  gates: [
    // NO mul gate ships on L3 (the finale divides) — the two RATE+ pairs carry the early and the
    // late trade instead, and the ÷3|÷2 inversion pair is the last trap before the standoff.
    { z: 26, left: { kind: 'num', value: 14 }, right: { kind: 'num', value: 5 } },
    // The early trade (measured live: no dominant pick across the two bots — level3.test.ts).
    { z: 44, left: { kind: 'num', value: 8 }, right: { kind: 'rate', value: 1 } },
    { z: 72, left: { kind: 'div', value: 2 }, right: { kind: 'num', value: 12 } },
    { z: 105, left: { kind: 'num', value: 16 }, right: { kind: 'num', value: 6 } },
    { z: 140, left: { kind: 'div', value: 3 }, right: { kind: 'num', value: 22 } },
    { z: 180, left: { kind: 'num', value: 25 }, right: { kind: 'num', value: -15 } },
    { z: 220, left: { kind: 'div', value: 2 }, right: { kind: 'num', value: 28 } },
    { z: 265, left: { kind: 'num', value: 12 }, right: { kind: 'num', value: 40 } },
    { z: 310, left: { kind: 'div', value: 2 }, right: { kind: 'num', value: 36 } },
    // The late bet, 22 m before the inversion finale (measured: it pulls the NOVICE side of the
    // trade — RATE ends bigger for the novice, the lane-geometry side effect).
    { z: 360, left: { kind: 'rate', value: 1 }, right: { kind: 'num', value: 16 } },
    // Last pair = the printed-inversion trap (channel c), the L2-420 pattern at its most expensive.
    { z: 405, left: { kind: 'div', value: 3 }, right: { kind: 'div', value: 2 } },
    { z: 435, left: { kind: 'num', value: 42 }, right: { kind: 'num', value: -35 } },
  ],
  crates: [
    // Smart's tier-3 ladder (measured: smart breaks all three; the novice only ever reaches 200).
    { z: 60, hp: 6, x: 0.55 },
    { z: 200, hp: 8, x: -0.55 },
    { z: 296, hp: 10, x: -0.55 },
  ],
  enemies: [
    // Lane-held blocks (the L2 pattern): contact needs footprint overlap, wiping pays a pickup.
    { z: 125, count: 14, x: 0.55, halfWidth: 0.4 },
    { z: 250, count: 16, x: -0.5, halfWidth: 0.4 },
    { z: 370, count: 18, x: 0.5, halfWidth: 0.45 },
  ],
  hazards: [
    // Measured: greedy eats ALL five (−98); every dodging bot clears all five (validation plus
    // hazardDodgeViolations, and the twin counterfactual in level3.test.ts).
    { z: 88, x: -0.5, halfWidth: 0.3, penalty: 18 }, // on greedy's left hold (the z-105 pull)
    { z: 160, x: 0.0, halfWidth: 0.3, penalty: 18 }, // centre squeeze #1
    // Centre squeeze #2. TUNED in Phase 2 (D2 mandate): the draft's z 245 put the crossing inside
    // the block-250 pickup pull and billed smart its only hazard hit (−20); z 235 (±15 m budget,
    // the plan's first candidate) frees the escape — re-measured 0 hits, every other run unchanged.
    { z: 235, x: 0.0, halfWidth: 0.3, penalty: 20 },
    { z: 330, x: 0.0, halfWidth: 0.3, penalty: 20 }, // centre squeeze #3
    { z: 385, x: -0.5, halfWidth: 0.3, penalty: 22 }, // on greedy's ÷3-lane hold (the inversion lane)
  ],
  obstacles: [
    // Benign for the named bots: sits on the lane gun line and is popped far ahead (the blast is
    // decor for the z-88 band crossing; the twin/greedy pay nothing here).
    { z: 100, x: -0.5, halfWidth: 0.15, hp: 16, kind: 'barrel' },
    // SIZE-SELECTIVE TRAP (the L2 barrel-#0 lesson, size axis): the small t1 crowd's crossing
    // columns pop it and its blast catches the body walking in (greedy −20 measured); the big
    // settled squads' reach (≥ 0.39) clears it from the lane with no bill (smart/twin 0).
    { z: 118, x: 0.28, halfWidth: 0.15, hp: 16, kind: 'barrel' },
    // Mid-bridge pinch: nobody's gun line, but greedy's body width walks into the blast lane
    // (measured: popped from range by the crossing sweep, lost 0 — the pinch reads as geometry).
    { z: 205, x: 0.05, halfWidth: 0.15, hp: 16, kind: 'barrel' },
    // Steer-around wall on the crate-296 detour (the bots that want the bazooka walk past it).
    { z: 275, x: -0.6, halfWidth: 0.35, hp: 28, kind: 'barricade' },
    // Gun-line pop (the ranged-detonation texture; no named bot pays it).
    { z: 350, x: 0.35, halfWidth: 0.15, hp: 16, kind: 'barrel' },
    // The final centre squeeze before the standoff (the L2-437 pattern, the TIGHTEST legal dodge:
    // safe centres only |x| ≥ 0.87). Smart breaches it under fire; the novice pays the −14 wall bill.
    { z: 425, x: 0.0, halfWidth: 0.3, hp: 28, kind: 'barricade' },
  ],
  // Plan 19 D1/D2: the low-flying HELI — the tank's aim-fire cycle verbatim (bossUsesFireCycle),
  // every number harder: 2.8 s cadence, 1.0 s lock, 80-damage shells, the 0.65/3.6 strafe on a
  // 0.24 hull. shellFlight 0.35 keeps the LOCK the honest deadline (P19-0 §fix-1: at 0.45 any
  // policy-controlled bot escapes from dead centre and shells could only ever hit bots that never
  // move). hp 1000 by the bisection above — the D2 pin, rules (a)/(b′)/(e) + floor/ceiling.
  boss: {
    kind: 'heli', z: 470, hp: 1000, standoff: 18, slamPeriod: 2.8, slamTelegraph: 1.0, slamDamage: 80,
    strafeHalfWidth: 0.65, strafePeriod: 3.6, hullHalfWidth: 0.24, shellFlight: 0.35,
  },
  weapons: WEAPONS,
};

/** Every level the game ships. Validation, cross-level property tests and `?level=` share this. */
export const SHIPPED_LEVELS: LevelSpec[] = [LEVEL_1, LEVEL_2, LEVEL_3];
